(ns practitest.frontend.batch-modal.fields
  (:require
   [clojure.string :as str]
   [practitest.frontend.batch-modal.events :as batch-modal-events]
   [practitest.frontend.batch-modal.subs :as batch-modal-subs]
   [practitest.frontend.common.bugtrackers :as bugtrackers]
   [practitest.frontend.common.format-utils :as fu]
   [practitest.frontend.common.i18n :refer [get-translation format-translation]]
   [practitest.frontend.common.mixpanel :as mixpanel]
   [practitest.frontend.common.permissions :as permissions]
   [practitest.frontend.common-entities.metadata :as metadata]
   [practitest.frontend.components.datepicker :as datepicker]
   [practitest.frontend.components.datepicker2 :as datepicker2]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components.markdown :as markdown]
   [practitest.frontend.components.overflow :as overflow]
   [practitest.frontend.components.selector :as selector]
   [practitest.frontend.components2.test-type :as test-type]
   [practitest.frontend.events :as events]
   [practitest.frontend.forms3.entity-form.fields :as entity-form-fields]
   [practitest.frontend.kanban :as kanban]
   [practitest.frontend.subs :as subs]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]))

(def ^:private batch-excluded-fields
  (into #{:created_at :updated_at :run_status :closed_at :closed_by :preconditions
          :requirement_status :parent_id :last_run :tester :defect_age :value_score
          :version_label :estimated_duration :duration_average
          :actual_duration :author :name :title :description
          :charter :guide_points}
        test-type/automated-test-fields))

(defn get-batch-fields
  ([entity] (get-batch-fields entity :edit))
  ([entity mode]
   (let [fields-by-name @(re-frame/subscribe [::subs/fields])
         entity-fields @(re-frame/subscribe [::subs/entity-fields entity])
         kanban-enabled? @(re-frame/subscribe [::subs/kanban-enabled?])
         kanban-supported-entities #{:test :issue :testset :requirement}
         excluded (cond-> batch-excluded-fields (= mode :clone) (disj :author))]
     (->> (keys entity-fields)
          (remove excluded)
          (keep (fn [field-id]
                  (when-let [meta (get fields-by-name field-id)]
                    (-> (merge meta (get entity-fields field-id) {:id field-id})
                        (assoc :field-db-id (:id meta))))))
          (concat (when (and kanban-enabled? (kanban-supported-entities entity))
                    [(assoc kanban/kanban-field :id :kanban_lane)]))
          (sort-by (fn [f] [(or (:order f) 0) (name (:id f))]))
          vec))))

(defn- select-items [possible-values]
  (let [items (vec possible-values)]
    (if (map? (first items))
      {:items items :label-fn :label :key-fn :id}
      {:items items :label-fn identity :key-fn identity})))

(defn- blank-value? [field-type value]
  (case field-type
    (:list :linkedlist :user :folder_id :date :date-time :milestone) (nil? value)
    (:multilist :tags :linked_milestones) (empty? value)
    (:checkbox :boolean) false
    (str/blank? value)))

(defn- common-raw-value [field common-values]
  (if (:custom? field)
    (get-in common-values [:custom (keyword (str (:field-db-id field)))])
    (get-in common-values [:system (:id field)])))

(defn- common-value->field-value [field raw]
  (when (some? raw)
    (case (:type field)
      :list
      (let [{:keys [items key-fn]} (select-items (:possible-values field))]
        (some #(when (= (str (key-fn %)) (str raw)) %) items))

      :multilist
      (set (filter (set (:possible-values field)) raw))

      :milestone
      (some (fn [[id label]] (when (= (str id) (str raw)) {:id id :label label}))
            (:possible-values field))

      (:user :checkbox :boolean :text :number :num_of_results :date :date-time :memo)
      raw

      nil)))

(defn- effective-value [field field-values common-values]
  (let [entry (get field-values (:id field))]
    (if (:dirty? entry)
      (:value entry)
      (common-value->field-value field (common-raw-value field common-values)))))

(defn mandatory-errors [fields field-values common-values]
  (reduce (fn [errors field]
            (let [{:keys [id type mandatory?]} field
                  checked? (:checked? (get field-values id))
                  value (effective-value field field-values common-values)]
              (if (and checked? mandatory? (blank-value? type value))
                (assoc errors id (get-translation "batch-modal.field.error.required" "This field is required"))
                errors)))
          {}
          fields))

;; Mirrors every entity controller's own strong-params method (qtest_design_params,
;; issue_params, requirement_params, suite_params, instance_params) - a couple
;; of batch fields are submitted under a different name than their frontend
;; field id. Identical across entities; instances simply have no :tags field.
(def ^:private system-field-renames
  {:tags "all_tags_list"
   :assigned_to "assigned_to_update"})

;; Each entity's strong-params method above is keyed by that entity's own
;; param name (params[:qtest_design], params[:issue], params[:suite], etc.),
;; matching entity_class.to_s.underscore. Derived from :db-entity rather than
;; a second hand-maintained map, so it can't drift out of sync with it.
(defn- entity-param-key [entity]
  (-> (get-in metadata/entities-metadata [entity :db-entity])
      (str/replace #"([a-z0-9])([A-Z])" "$1_$2")
      str/lower-case
      keyword))

(defn- item-value [v]
  (if (map? v) (:id v) v))

(defn- item-label [v]
  (if (map? v) (:label v) v))

;; Matches forms3/entity_form/fields.cljs's :list/:multilist on-change - the
;; backend parses this as a JSON-stringified array of labels, not ids.
(defn- multilist-value [value]
  (.stringify js/JSON (clj->js (mapv item-label value))))

(defn- submit-value [{:keys [id type custom?]} value]
  (case type
    :tags (str/join "," (map item-value value))
    :multilist (multilist-value value)
    :linked_milestones (mapv item-value value)
    :user (if (and (= id :assigned_to) (not custom?))
            nil ;; handled separately by events/process-assigned-to
            (str (item-value value)))
    (:list :linkedlist :milestone :kanban_lane) (item-value value)
    ;; Matches checkbox-control-editing/-readonly's own (= value "yes") check.
    (:checkbox :boolean) (if (= value "yes") "yes" "no")
    value))

;; Issue Status is not part of issue_params/entity_params at all - the backend
;; reads it as its own top-level batch_issue_status_id param and applies it via
;; Issue#batch_change_issue_status (admin-only, skips the normal workflow
;; transition check), not mass-assignment like every other field.
;; kanban_lane is the same story - it's not a real system field either, the
;; backend reads it as its own top-level "kanban" param (BatchEditService).
(defn build-submit-params
  [entity fields field-values common-values selection current-view grid-filter set-id
   comment disable-notifications? preserve-tags?]
  (let [checked-fields (filterv #(:checked? (get field-values (:id %))) fields)
        issue-status-field (when (= entity :issue)
                             (some #(when (= (:id %) :issue_status) %) checked-fields))
        kanban-field (some #(when (= (:id %) :kanban_lane) %) checked-fields)
        milestones-field (when (contains? #{:issue :requirement} entity)
                           (some #(when (= (:id %) :linked_milestones) %) checked-fields))
        checked-fields (remove #(contains? #{issue-status-field kanban-field milestones-field} %) checked-fields)
        {custom-fields true system-fields false} (group-by (comp boolean :custom?) checked-fields)
        field-value (fn [f] (effective-value f field-values common-values))
        ;; process-assigned-to is the same function the New Test/Run form's
        ;; save flow uses to encode :assigned_to as "User-123"/"Group-45".
        assigned-to-params (events/process-assigned-to
                            (into {} (map (fn [f] [(:id f) (field-value f)])) system-fields))
        entity-params (merge (reduce (fn [acc f]
                                       (assoc acc
                                              (get system-field-renames (:id f) (name (:id f)))
                                              (submit-value f (field-value f))))
                                     {}
                                     system-fields)
                             assigned-to-params)
        custom (reduce (fn [acc f]
                         (assoc acc (str (:field-db-id f)) (submit-value f (field-value f))))
                       {}
                       custom-fields)
        tags-checked? (some #(= :tags (:id %)) checked-fields)]
    ;; params.require(entity_param_key) raises ParameterMissing on an empty
    ;; hash (not just a missing key) - only send it when there's something in it.
    (cond-> (merge {:custom custom
                    :add_comment_to_all comment
                    :disable_notifications disable-notifications?}
                   (batch-modal-events/selection-params selection current-view grid-filter))
      (seq entity-params) (assoc (entity-param-key entity) entity-params)
      issue-status-field (assoc :batch_issue_status_id
                                (submit-value issue-status-field (field-value issue-status-field)))
      kanban-field (assoc :kanban (submit-value kanban-field (field-value kanban-field)))
      milestones-field (assoc :milestones (submit-value milestones-field (field-value milestones-field)))
      (and tags-checked? preserve-tags?) (assoc :do_not_override_tags "1")
      set-id (assoc :set_id set-id))))

(defn scroll-to-field! [field-id]
  (when-let [el (js/document.getElementById (str "batch-field-" (name field-id)))]
    (.scrollIntoView el #js {:behavior "smooth" :block "center"})))

(defn- track-value-edited! [entity field-id]
  (mixpanel/track "Batch Edit"
                  {"Action" "Field Value Edited"
                   "Entity" (mixpanel/entity-type->mixpanel entity)
                   "Field" (name field-id)}))

(defn- track-field-checked! [entity field-id checked?]
  (mixpanel/track "Batch Edit"
                  {"Action" (if checked? "Field Selected" "Field Deselected")
                   "Entity" (mixpanel/entity-type->mixpanel entity)
                   "Field" (name field-id)}))

(defn- on-value-change
  ([entity field-id checked?] (on-value-change entity field-id checked? nil))
  ([entity field-id checked? linked-child-ids]
   (fn [value]
     (re-frame/dispatch [::batch-modal-events/set-field-value field-id value])
     (when-not checked?
       (track-field-checked! entity field-id true))
     ;; A linkedlist field's own possible-values depend on this field's value -
     ;; once it changes, any previously-picked child value is no longer valid.
     (doseq [child-id linked-child-ids]
       (re-frame/dispatch [::batch-modal-events/clear-field-value child-id]))
     (track-value-edited! entity field-id))))

(defn- field-input-id [field-id]
  (str "batch-field-value-" (name field-id)))

(defn- status-workflow-locked? [entity field]
  (and (not (permissions/project-administrator? @(re-frame/subscribe [::subs/current-project])))
       (cond
         (and (= entity :issue) (= (:id field) :issue_status)) true
         
         (and (= entity :test) (= (:id field) :test_status))
         (and @(re-frame/subscribe [::subs/use-test-workflow?])
              (not @(re-frame/subscribe [::subs/test-status-default-workflow?])))
         
         :else false)))

(defn- disabled-reason [entity field]
  (when-let [reason (cond
                       (and (:bugtracker field)
                            @(re-frame/subscribe [::batch-modal-subs/integrated-entities?]))
                       (format-translation
                        (get-translation "batch-modal.field.disabled.bugtracker-mapped" "%{bugtracker} Mapped Field")
                        :bugtracker (bugtrackers/get-label (:integrated_bugtracker @(re-frame/subscribe [::subs/current-project]))))

                       (or (:read-only? field) (= :scenario (:id field)))
                       (get-translation "view-editor.combined.read-only" "Read Only")

                       (status-workflow-locked? entity field)
                       (get-translation "batch-modal.field.disabled.status-workflow" "Status Workflow Enabled"))]
    (str "( " reason " )")))

;; base-control (and base-list-control, which wraps it) is the same wrapper
;; the New Test/Run form uses for every field - it renders the label
;; (with the mandatory asterisk), the read-only vs. editing toggle, and the
;; error message, all from this one props map. field-row no longer renders
;; any of that itself.
(defn- base-props [entity field field-errors]
  (let [{:keys [id label mandatory? custom?]} field
        reason (disabled-reason entity field)
        field-id (field-input-id id)
        label-content (if reason [:span.disabled-label label " " reason] label)
        tooltip-text (if reason (str label " " reason) label)]
    {:field-id field-id
     :id id
     :custom? custom?
     :label [overflow/overflow-with-mux-tooltip {}
             (fn [props] [:span.m-label-text props label-content])
             tooltip-text]
     :mandatory? mandatory?
     :read-only? (boolean reason)
     :get-field-state (fn [_]
                        (reagent/atom
                         {:errors (when-let [msg (get field-errors id)]
                                    [{:message msg}])}))}))

(defn- preserve-tags-row []
  (let [preserve? @(re-frame/subscribe [::batch-modal-subs/preserve-tags?])]
    [:div.field-row.checkbox
     [:input.m-checkbox
      {:type "checkbox"
       :id "batch-modal-preserve-tags"
       :checked preserve?
       :on-change #(re-frame/dispatch [::batch-modal-events/toggle-preserve-tags])}]
     [:label.checkbox-label {:for "batch-modal-preserve-tags"}
      (get-translation "batch-modal.field.tags.preserve" "Preserve existing tags")]]))

(defn- field-editor [entity field value field-errors checked? linked-child-ids parent-value]
  (let [{:keys [id type possible-values]} field
        props (base-props entity field field-errors)
        on-change (on-value-change entity id checked? linked-child-ids)]
    ;; Batch edit has no common-value prefill, so the normal read-only view
    ;; (label-fn value) would render blank. Use a disabled placeholder only
    ;; for the base-list-control types (they render as a dropdown when
    ;; editable) - everything else renders sensibly with a nil value via
    ;; its own case branch below.
    (if (and (:read-only? props) (contains? #{:list :multilist :linkedlist :user} type))
      (let [placeholder [:span.disabled-select
                         [:span (get-translation "generic.list.placeholder" "Select value")]
                         [:span.icon-holder [icons/mux-chevron-down2]]]]
        [entity-form-fields/base-control
         (assoc props :custom-control? true)
         placeholder
         placeholder])
      (case type
        :memo
        [entity-form-fields/base-control
         props
         [markdown/markdown-editor {:editing? true :id (:field-id props) :value value :on-change on-change :max-height 120}]
         [markdown/markdown-editor {:read-only? true :value value}]]

        (:checkbox :boolean)
        [:<>
         [:div.m-checkbox-container
          (if (:read-only? props)
            [entity-form-fields/checkbox-control-readonly {:value value :field-id (:field-id props)}]
            [entity-form-fields/checkbox-control-editing
             {:value value
              :field-id (:field-id props)
              :on-change #(on-change (if (-> % .-target .-checked) "yes" "no"))}])
          [:label.m-label {:for (:field-id props)}
           (:label props)
           (when (:mandatory? props) [:span.mandatory "*"])]]
         (when-let [msg (get field-errors id)]
           [:div.validation-error-message msg])]

        :folder_id
        [entity-form-fields/base-control
         (assoc props :custom-control? true)
         [entity-form-fields/folder-input {:on-change on-change :value value :entity-type entity}]
         [:span (str value)]]

        :tags
        (let [project-tags @(re-frame/subscribe [::subs/tags])
              items (mapv (fn [[tag _count]] {:id tag :label tag}) project-tags)]
          [:<>
           [entity-form-fields/base-control
            props
            [selector/new-select
             {:items items
              :id (:field-id props)
              :label-fn :label
              :key-fn :id
              :multi? true
              :tags? true
              :on-add-value (fn [new-tag]
                              (when-let [new-tag (fu/validate-new-list-value! new-tag)]
                                (re-frame/dispatch [::entity-form-fields/add-tags (set [new-tag])])))
              :placeholder (get-translation "generic.list.placeholder" "Select value")
              :value (set value)
              :on-change on-change}]
            [:span (str/join ", " (map item-label value))]]
           [preserve-tags-row]])

        ;; A linkedlist's real possible-values are keyed by its parent field's
        ;; current value (:possible-values {parent-value [child-values]}), not
        ;; a flat list - and until the parent has a value there are none at
        ;; all, matching the parent field's own required-first-edit behavior.
        :linkedlist
        (let [options (when parent-value (get possible-values parent-value))
              {:keys [items label-fn key-fn]} (select-items options)]
          [entity-form-fields/base-list-control
           (assoc props
                  :type type
                  :multi? false
                  :possible-values items
                  :label-fn label-fn
                  :key-fn key-fn
                  :value value
                  :on-change on-change)])

        :user
        (let [users @(re-frame/subscribe [::subs/users])
              groups @(re-frame/subscribe [::subs/groups])
              items (if (= id :assigned_to) (vec (concat users groups)) (vec users))]
          [entity-form-fields/base-list-control
           (assoc props
                  :type type
                  :multi? false
                  :possible-values items
                  :label-fn :name
                  :key-fn (fn [u] (str (:type u) "-" (:id u)))
                  :value value
                  :on-change on-change)])

        :kanban_lane
        (let [lanes @(re-frame/subscribe [::kanban/lanes])
              {:keys [items label-fn key-fn]} (select-items (map (fn [lane] {:id (:id lane) :label (:name lane)}) lanes))]
          [entity-form-fields/base-list-control
           (assoc props
                  :type type
                  :multi? false
                  :possible-values items
                  :label-fn label-fn
                  :key-fn key-fn
                  :value value
                  :on-change on-change)])

        (:list :multilist)
        (let [{:keys [items label-fn key-fn]} (select-items possible-values)]
          [entity-form-fields/base-list-control
           (assoc props
                  :type type
                  :multi? (= type :multilist)
                  :possible-values items
                  :label-fn label-fn
                  :key-fn key-fn
                  :value value
                  :on-change on-change)])

        ;; :milestone (Suite's single milestone) and :linked_milestones (Issue/Requirement's
        ;; multi) both carry their possible-values as raw [id label] tuples, not maps.
        (:milestone :linked_milestones)
        (let [{:keys [items label-fn key-fn]} (select-items (map (fn [[id label]] {:id id :label label})
                                                                  possible-values))]
          [entity-form-fields/base-list-control
           (assoc props
                  :type type
                  :multi? (= type :linked_milestones)
                  :possible-values items
                  :label-fn label-fn
                  :key-fn key-fn
                  :value value
                  :on-change on-change)])

        :date
        [entity-form-fields/base-control
         (assoc props :custom-control? true)
         [datepicker2/date-picker-input
          {:id (:field-id props)
           :class "m-input"
           :time? false
           :value value
           :on-change on-change}]
         [:span (fu/formatted-local-date value)]]

        :date-time
        [entity-form-fields/base-control
         (assoc props :custom-control? true)
         [datepicker/instant-picker
          {:field-id (:field-id props)
           :class "form-control m-input"
           :value value
           :on-change on-change}]
         [:span (fu/formatted-date-time value)]]

        ;; :text, :number, :num_of_results and anything else. forms3's own
        ;; text-input isn't reusable here - its :on-change is hardcoded to the
        ;; global form-state2 system, not a pluggable callback.
        [entity-form-fields/base-control
         props
         [:input.m-input
          {:id (:field-id props)
           :value (or value "")
           :type (if (= type :number) "number" "text")
           :on-change #(on-change (-> % .-target .-value))}]
         [:span (str value)]]))))

(defn field-row [entity field fields]
  (let [field-values @(re-frame/subscribe [::batch-modal-subs/field-values])
        field-errors @(re-frame/subscribe [::batch-modal-subs/field-errors])
        common-values @(re-frame/subscribe [::batch-modal-subs/common-values])
        checked? (:checked? (get field-values (:id field)))
        value (when-not (disabled-reason entity field)
                (effective-value field field-values common-values))
        error (get field-errors (:id field))
        linked-child-ids (->> fields
                              (filter #(= (:id field) (:parent_list_field_name %)))
                              (mapv :id))
        ;; A list field's stored value is the raw selected item map
        ;; ({:id ... :label ...}), not a bare id - item-value unwraps it the
        ;; same way submit-value does.
        parent-value (when-let [parent-id (:parent_list_field_name field)]
                       (when-let [parent-field (some #(when (= parent-id (:id %)) %) fields)]
                         (item-value (effective-value parent-field field-values common-values))))
        apply-checkbox [:input.m-checkbox
                        {:type "checkbox"
                         :disabled (boolean (disabled-reason entity field))
                         :checked (boolean checked?)
                         :on-change (fn [e]
                                      (let [checked (-> e .-target .-checked)]
                                        (re-frame/dispatch [::batch-modal-events/set-field-checked (:id field) checked])
                                        (track-field-checked! entity (:id field) checked)))}]
        body [:div.field-body
              [field-editor entity field value field-errors checked? linked-child-ids parent-value]]]
    [:div.grid-row
     {:id (str "batch-field-" (name (:id field)))
      :class (when error "grid-row--error")}
       [:<> apply-checkbox body]]))
